import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { apiErr, STATUS_LABELS } from "@/lib/api";
import { fl } from "@/lib/finI18n";
import { coaName } from "@/lib/masterI18n";
import { ComboBox } from "@/components/ComboBox";
import { LedgerInput } from "@/components/LedgerInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, X, Pencil, CheckCheck } from "lucide-react";

const STATUS_ROLES = { super_admin: "all", supervisor: "all", leader: "no-archive" };

function report(data, verb, lang) {
  const n = data.deleted ?? data.updated ?? 0;
  if (n) toast.success(`${n} ${fl("bulkTxn", lang)} ${verb}`);
  if (data.recalculated) toast.info(`${data.recalculated} ${fl("recalcToast", lang)}`);
  if (data.skipped?.length) {
    const lines = data.skipped.slice(0, 4).map((s) => `${s.ref}: ${s.reason}`).join("\n");
    toast.warning(`${data.skipped.length} ${fl("bulkSkipped", lang)}\n${lines}${data.skipped.length > 4 ? "\n…" : ""}`, { duration: 9000 });
  }
}

function EditDialog({ open, onOpenChange, clientId, coaList, ids, onDone }) {
  const { lang } = useI18n();
  const [coa, setCoa] = useState("");
  const [vendor, setVendor] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/transactions/bulk-update", { client_id: clientId, ids, coa_id: coa || null, vendor: vendor.trim() || null });
      report(data, fl("bulkUpdated", lang), lang);
      setCoa(""); setVendor(""); onOpenChange(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="txn-bulk-edit-dialog">
        <DialogHeader><DialogTitle>{fl("bulkEditTitle", lang)} ({ids.length})</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>COA</Label>
            <ComboBox value={coa} onChange={setCoa} testid="txn-bulk-coa" placeholder={fl("bulkKeep", lang)}
              options={coaList.map((c) => ({ value: c.id, label: `${c.code} · ${coaName(c, lang)}` }))} />
          </div>
          <div className="space-y-1.5"><Label>Ledger</Label>
            <LedgerInput clientId={clientId} value={vendor} onChange={setVendor} onPick={(l) => { setVendor(l.name); if (l.coa_id && !coa) setCoa(l.coa_id); }}
              testid="txn-bulk-ledger" placeholder={fl("bulkKeep", lang)} />
          </div>
          <p className="text-xs text-muted-foreground">{fl("bulkEditNote", lang)}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="txn-bulk-edit-cancel">{fl("cancel", lang)}</Button>
          <Button onClick={save} disabled={saving || (!coa && !vendor.trim())} data-testid="txn-bulk-edit-save">{fl("bulkApply", lang)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TxnBulkBar({ clientId, ids, coaList, onClear, onDone }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  if (!ids.length) return null;
  const statusMode = STATUS_ROLES[user.role];
  const statuses = Object.keys(STATUS_LABELS).filter((s) => statusMode === "all" || s !== "archived");
  const canEdit = user.role !== "staff_pajak";
  const run = async (url, body, verb) => {
    setBusy(true);
    try { const { data } = await api.post(url, { client_id: clientId, ids, ...body }); report(data, verb, lang); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = () => { if (window.confirm(`${fl("bulkDelTxnQ", lang)} (${ids.length})`)) run("/transactions/bulk-delete", {}, fl("bulkDeletedV", lang)); };
  const applyStatus = () => { if (status) run("/transactions/bulk-status", { status }, `→ ${STATUS_LABELS[status]}`); };
  return (
    <div className="sticky top-2 z-20 flex flex-wrap items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 backdrop-blur" data-testid="txn-bulk-bar">
      <span className="text-sm font-medium mr-1" data-testid="txn-bulk-count">{ids.length} {fl("bulkTxnSel", lang)}</span>
      {statusMode && (
        <div className="flex items-center gap-1.5">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 w-40" data-testid="txn-bulk-status"><SelectValue placeholder={fl("bulkStatusPh", lang)} /></SelectTrigger>
            <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s} data-testid={`txn-bulk-status-${s}`}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" className="h-8" disabled={!status || busy} onClick={applyStatus} data-testid="txn-bulk-status-apply"><CheckCheck className="h-4 w-4 mr-1" />{fl("bulkApply", lang)}</Button>
        </div>
      )}
      {canEdit && <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => setEditOpen(true)} data-testid="txn-bulk-edit"><Pencil className="h-4 w-4 mr-1" />{fl("bulkEditBtn", lang)}</Button>}
      <Button size="sm" variant="destructive" className="h-8" disabled={busy} onClick={del} data-testid="txn-bulk-delete"><Trash2 className="h-4 w-4 mr-1" />{fl("del", lang)}</Button>
      <Button size="sm" variant="ghost" className="h-8 ml-auto" onClick={onClear} data-testid="txn-bulk-clear"><X className="h-4 w-4 mr-1" />{fl("cancel", lang)}</Button>
      <EditDialog open={editOpen} onOpenChange={setEditOpen} clientId={clientId} coaList={coaList} ids={ids} onDone={onDone} />
    </div>
  );
}
